// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './Navbar.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { getDocs, collection, doc } from 'firebase/firestore';
import { auth, firestore, storage } from '../../context/firebase';

import { getNumRequests } from '../../shared_components/LabelingRequests/LabelingRequests';

import logo_circle from '../../assets/logos/upcircle_logo_circle.png';
import logout_icon from '../../assets/icons/white/logout.png';
import conveyor_belt_icon from '../../assets/icons/white/conveyor_belt.png';
import label_icon from '../../assets/icons/white/label.png';
import close_icon from '../../assets/icons/white/close.svg';
import { fetchImageThumbnailUrl } from '../../shared_components/utils/ImgUtils';
import { User } from 'lucide-react';
import { LABELING_ROUTE } from '../../constant/routes';
import { CLIENTS_COLLECTION_PATH, USER_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../../stores/authStore';
import { useUpdateDocument } from '../../shared_components/hooks/useUpdateDocument';

const Navbar = ({ showConveyorFeed, setShowConveyorFeed }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const currentUserUid = useAuthStore((state) => state.currentUser?.uid);
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const currentUserUserFlags = useAuthStore((state) => state.currentUser?.info?.user_flags);
  const currentUserDisplayName = useAuthStore((state) => state.currentUser?.info?.displayName);
  const currentUserPhotoURL = useAuthStore((state) => state.currentUser?.info?.photoURL);
  const currentUserDevices = useAuthStore((state) => state.currentUser?.devices);
  const deliverySettings = useAuthStore((state) => state.deliverySettings);
  const [clients, setClients] = React.useState({});
  const [numLabelRequests, setNumLabelRequests] = React.useState(0);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [profileImageUrl, setProfileImageUrl] = React.useState(null);
  const userRef = doc(firestore, USER_DOCUMENT_PATH(currentUserUid));
  const { updateDocument: updateUser } = useUpdateDocument(userRef);

  React.useEffect(() => {
    /* If the user is an admin, get a list of all available clients */
    async function getClients() {
      const clientsNew = {};
      const snapshots = await getDocs(collection(firestore, CLIENTS_COLLECTION_PATH));
      snapshots.forEach((doc) => {
        clientsNew[doc.id] = doc.data();
      });
      setClients(clientsNew);
    }

    if (currentUserUserFlags && currentUserUserFlags.includes('admin')) {
      getClients();
    }

    if (currentUserUserFlags && ['labeler', 'admin'].some((flag) => currentUserUserFlags.includes(flag))) {
      getNumRequests(firestore, currentUserClientId, currentUserUid).then((num) => setNumLabelRequests(num));
    }
  }, []);

  const fetchProfileImageUrl = async () => {
    if (!currentUserPhotoURL) {
      setProfileImageUrl(null);
      return;
    }
    const imageUrl = await fetchImageThumbnailUrl(storage, currentUserPhotoURL, '_200x200');
    setProfileImageUrl(imageUrl);
  };

  React.useEffect(() => {
    fetchProfileImageUrl();
  }, [currentUserPhotoURL]);

  async function handleClientChange(client) {
    /* Change the user's company (For admins only) */
    await updateUser({ company: client });
    // Refresh the page to apply changes
    window.location.reload(false);
  }

  async function handleLanguageChange(e) {
    // Handle language change
    const selectedLanguage = e.target.value;
    i18n.changeLanguage(selectedLanguage);

    // Update the language code for the user
    await updateUser({ languageCode: selectedLanguage });
  }

  return (
    <div className='navbar'>
      <Link className='logo-button' to='/'>
        <img src={logo_circle} className='logo' alt='logo' />
      </Link>
      {/* Show labeling button if the user is a labeler or admin and feature is enabled */}
      {deliverySettings?.features?.mobile_labeling_requests &&
        currentUserUserFlags &&
        ['labeler', 'admin'].some((flag) => currentUserUserFlags.includes(flag)) && (
          <button
            className='labeling-button'
            onClick={() => {
              navigate(LABELING_ROUTE);
            }}
          >
            <img src={label_icon} style={{ opacity: numLabelRequests > 0 ? 1 : 0.6 }} />
            {numLabelRequests > 0 && <div className='num-labels'>{numLabelRequests}</div>}
          </button>
        )}

      {/* Show conveyor feed button if the feature is enabled */}
      {deliverySettings?.features?.bale_feed_view && currentUserDevices && (
        <button
          className={`show-feed-button ${showConveyorFeed && 'show'}`}
          onClick={() => {
            setShowConveyorFeed((prev) => !prev);
          }}
        >
          <img src={conveyor_belt_icon} />
        </button>
      )}

      {/* Show user settings */}
      <div className='user'>
        <select
          className='client-select-dropdown language-select'
          value={i18n.language}
          onChange={handleLanguageChange}
        >
          <option value='en'>EN</option>
          <option value='de'>DE</option>
        </select>
        {currentUserUserFlags && currentUserUserFlags.includes('admin') && (
          <>
            <select
              className='client-select-dropdown company-select'
              value={currentUserClientId}
              onChange={(e) => {
                handleClientChange(e.target.value);
              }}
            >
              {Object.keys(clients).map((id) => {
                return (
                  <option key={id} value={id}>
                    {clients[id].company_displayname}
                  </option>
                );
              })}
            </select>
          </>
        )}

        <p className='divider'>|</p>
        <span className='hello-text'>
          {t('navbar_hello')}, {currentUserDisplayName}
        </span>
        {profileImageUrl ? (
          <img
            src={profileImageUrl}
            alt={currentUserDisplayName}
            className='user_photo_img'
            onClick={() => setShowUserMenu((prev) => !prev)}
          />
        ) : (
          <User onClick={() => setShowUserMenu((prev) => !prev)}></User>
        )}
        <button onClick={() => signOut(auth)} style={{ display: 'none' }} id='logout_icon' />
        <label htmlFor='logout_icon'>
          <img src={logout_icon} alt='logout icon' className='logout_icon_img' />
        </label>
      </div>

      {showUserMenu && (
        <div className='user-menu-overlay' onClick={() => setShowUserMenu((prev) => !prev)}>
          <div className='user-menu-popup' onClick={(e) => e.stopPropagation()}>
            <div className='popup-header'>
              <span className='hello-text'>
                {t('navbar_hello')}, {currentUserDisplayName}
              </span>
              <div className='close-button' onClick={() => setShowUserMenu((prev) => !prev)}>
                <img src={close_icon} />
              </div>
            </div>
            <div className='popup-options'>
              <select
                className='client-select-dropdown popup-language-select'
                value={i18n.language}
                onChange={handleLanguageChange}
              >
                <option value='en'>EN</option>
                <option value='de'>DE</option>
              </select>
              {currentUserUserFlags && currentUserUserFlags.includes('admin') && (
                <>
                  <select
                    className='client-select-dropdown popup-company-select'
                    value={currentUserClientId}
                    onChange={(e) => {
                      handleClientChange(e.target.value);
                    }}
                  >
                    {Object.keys(clients).map((id) => {
                      return (
                        <option key={id} value={id}>
                          {clients[id].company_displayname}
                        </option>
                      );
                    })}
                  </select>
                </>
              )}
              <button className='popup-logout-button' onClick={() => signOut(auth)}>
                <img src={logout_icon} alt='logout icon' className='logout_icon_img' />
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
